import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import {
  Heart,
  Eye,
  Share2,
  Download,
  Flag,
  ArrowLeft,
  Flame,
  Diamond,
  Zap,
  MessageSquare,
  Palette,
  Pencil
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar } from '../components/ui/avatar';
import { toast } from 'sonner';
import { playReactionAnimation } from '../utils/reactionAnimations';
import FollowButton from '../components/FollowButton';
import HumanVerifiedBadge from '../components/HumanVerifiedBadge';
import EditArtworkModal from '../components/EditArtworkModal';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL (http/https), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative URL starting with /, prepend backend URL
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise, assume it's a relative path
  return `${BACKEND_URL}/${url}`;
};

const ArtworkDetail = () => {
  const { postId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArtworks, setRelatedArtworks] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now()); // For cache busting

  const fetchArtwork = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/art/post/${postId}`);
      setArtwork(response.data);
      
      // Fetch related artworks by same artist
      if (response.data.user_id) {
        const relatedResponse = await axios.get(`${API}/art/user/${response.data.user_id}?limit=6`);
        setRelatedArtworks(relatedResponse.data.posts.filter(p => p.id !== postId));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching artwork:', error);
      toast.error('Failed to load artwork');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtwork();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  // Check if current user is the owner
  const isOwner = user && artwork && (artwork.user_id === user.id || artwork.creator_id === user.id);

  const handleArtworkUpdated = (updatedArtwork) => {
    setArtwork(updatedArtwork);
    setImageKey(Date.now()); // Force image refresh
    setShowEditModal(false);
  };

  const [userReaction, setUserReaction] = useState(null);
  const [reacting, setReacting] = useState(false);
  const pendingBtnRef = React.useRef(null);

  // Fetch user's existing reaction on load
  useEffect(() => {
    if (!user || !postId) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/reactions/mine?target_type=art_post&target_id=${postId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setUserReaction(res.data.reaction_type)).catch(() => {});
  }, [user, postId]);

  const handleReaction = async (reactionType) => {
    if (!user) {
      toast.error('Please sign in to react');
      return;
    }
    if (reacting) return;

    setReacting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('target_type', 'art_post');
      formData.append('target_id', postId);
      formData.append('reaction_type', reactionType);

      const res = await axios.post(`${API}/react`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      const { action, counts } = res.data;
      setUserReaction(res.data.reaction_type);
      setArtwork(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          reactions: counts
        }
      }));

      // Animate only on add/switch
      if ((action === 'added' || action === 'updated') && pendingBtnRef.current) {
        playReactionAnimation(pendingBtnRef.current, reactionType);
      }
      pendingBtnRef.current = null;

      if (action === 'added') toast.success('Reaction added!');
      else if (action === 'updated') toast.success('Reaction switched!');
      else if (action === 'removed') toast.success('Reaction removed');
    } catch (error) {
      console.error('Error adding reaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to add reaction');
    } finally {
      setReacting(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: artwork.title,
          text: artwork.description,
          url: url
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleDownload = () => {
    const rawImageUrl = artwork.image_url || artwork.images?.[0]?.url;
    const imageUrl = resolveImageUrl(rawImageUrl);
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    } else {
      toast.error('Image not available for download');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading artwork...</p>
        </div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-12 text-center">
            <h2 className="text-2xl font-bold mb-2">Artwork not found</h2>
            <p className="text-gray-400 mb-6">This artwork may have been removed or doesn&apos;t exist</p>
            <Link to="/art-gallery">
              <Button className="bg-purple-500 hover:bg-purple-600">
                Back to Gallery
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Back Button */}
      <div className="container mx-auto px-4 sm:px-6 py-6">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="container mx-auto px-4 sm:px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Artwork Display - Left/Center */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-900 border-gray-800 overflow-hidden">
              {/* Get image URL from images array or direct image_url field */}
              {(() => {
                const rawImageUrl = artwork.image_url || artwork.images?.[0]?.url;
                const imageUrl = resolveImageUrl(rawImageUrl);
                if (imageUrl) {
                  // Add cache-busting parameter
                  const cacheBustedUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${imageKey}`;
                  return (
                    <img
                      key={imageKey}
                      src={cacheBustedUrl}
                      alt={artwork.title}
                      className="w-full h-auto object-contain max-h-[80vh]"
                      onError={(e) => {
                        // Show placeholder on error but don't erase the URL
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  );
                }
                return null;
              })()}
              {/* Fallback placeholder - hidden by default */}
              <div 
                className="w-full h-64 bg-gray-800 flex items-center justify-center text-gray-500"
                style={{ display: resolveImageUrl(artwork.image_url || artwork.images?.[0]?.url) ? 'none' : 'flex' }}
              >
                <div className="text-center">
                  <Palette className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Image unavailable</p>
                </div>
              </div>
            </Card>

            {/* Reactions Bar */}
            <div className="flex items-center gap-3 mt-4" data-testid="reaction-buttons">
              {[
                { type: 'heart', Icon: Heart, color: 'pink', label: 'Love' },
                { type: 'fire', Icon: Flame, color: 'orange', label: 'Fire' },
                { type: 'diamond', Icon: Diamond, color: 'cyan', label: 'Diamond' },
              ].map(({ type, Icon, color, label }) => {
                const isSelected = userReaction === type;
                const count = artwork.metrics?.reactions?.[type] || 0;
                return (
                  <button
                    key={type}
                    onClick={(e) => {
                      pendingBtnRef.current = e.currentTarget;
                      handleReaction(type);
                    }}
                    disabled={reacting}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all disabled:opacity-50 ${
                      isSelected
                        ? `bg-${color}-500/20 border-${color}-500 ring-1 ring-${color}-500/50`
                        : 'bg-gray-900 border-gray-800 hover:border-gray-600'
                    }`}
                    data-testid={`reaction-btn-${type}`}
                    title={label}
                    aria-label={`${label}${isSelected ? ' (selected)' : ''}`}
                    aria-pressed={isSelected}
                  >
                    <Icon className={`w-5 h-5 ${isSelected ? `text-${color}-400` : `text-${color}-500`}`} />
                    <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info Sidebar - Right */}
          <div className="space-y-6">
            {/* Artist Info */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <Link to={`/u/${artwork.artist_handle}`}>
                    <Avatar className="w-16 h-16 bg-gray-800">
                      {artwork.artist_avatar ? (
                        <img src={artwork.artist_avatar} alt={artwork.artist_name} />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-gray-400 text-xl">
                          {artwork.artist_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </Avatar>
                  </Link>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Link to={`/u/${artwork.artist_handle}`} className="font-bold text-lg hover:text-purple-500 transition-colors">
                        {artwork.artist_name}
                      </Link>
                      {artwork.artist_verified && (
                        <HumanVerifiedBadge size="small" showText={false} />
                      )}
                    </div>
                    <p className="text-sm text-gray-400">@{artwork.artist_handle}</p>
                    {artwork.artist_tier && (
                      <Badge className="bg-purple-500/10 text-purple-400 mt-2">
                        {artwork.artist_tier}
                      </Badge>
                    )}
                  </div>
                </div>

                {artwork.user_id !== user?.id && (
                  <FollowButton
                    targetUserId={artwork.user_id}
                    targetHandle={artwork.artist_handle}
                    className="w-full"
                  />
                )}
              </CardContent>
            </Card>

            {/* Artwork Details */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <h1 className="text-2xl font-bold mb-3">{artwork.title || 'Untitled'}</h1>
                
                {artwork.description && (
                  <p className="text-gray-300 mb-4 whitespace-pre-wrap">
                    {artwork.description}
                  </p>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {formatNumber(artwork.metrics?.views)} views
                  </div>
                  <div className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    {formatNumber(artwork.metrics?.likes)} likes
                  </div>
                </div>

                {/* Date */}
                <p className="text-sm text-gray-500 mb-4">
                  Posted on {formatDate(artwork.created_at)}
                </p>

                {/* Tags */}
                {artwork.tags && artwork.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {artwork.tags.map((tag, index) => (
                      <Badge key={index} className="bg-purple-500/10 text-purple-400">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Category */}
                {artwork.category && (
                  <div className="mb-4">
                    <span className="text-sm text-gray-400">Category: </span>
                    <span className="text-sm text-white">{artwork.category}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {isOwner && (
                    <Button
                      onClick={() => setShowEditModal(true)}
                      className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                      data-testid="artwork-edit-btn"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  <Button
                    onClick={handleShare}
                    variant="outline"
                    className="flex-1 border-gray-700"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button
                    onClick={handleDownload}
                    variant="outline"
                    className="flex-1 border-gray-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    View Full
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Report Button */}
            <Button
              variant="ghost"
              className="w-full text-gray-500 hover:text-red-500"
            >
              <Flag className="w-4 h-4 mr-2" />
              Report Artwork
            </Button>
          </div>
        </div>

        {/* Related Artworks */}
        {relatedArtworks.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">More from this artist</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {relatedArtworks.map((related) => (
                <Link key={related.id} to={`/art/${related.id}`}>
                  <Card className="bg-gray-900 border-gray-800 hover:border-purple-500 transition-colors overflow-hidden group">
                    <img
                      src={related.image_url}
                      alt={related.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform"
                    />
                    <CardContent className="p-3">
                      <h3 className="text-sm font-medium truncate">{related.title || 'Untitled'}</h3>
                      <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                        <Eye className="w-3 h-3" />
                        {formatNumber(related.metrics?.views)}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Artwork Modal */}
      {showEditModal && (
        <EditArtworkModal
          artwork={artwork}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleArtworkUpdated}
        />
      )}
    </div>
  );
};

export default ArtworkDetail;
